import { NavLink, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
	Box,
	Typography,
	CircularProgress,
	Alert,
	Divider,
	Button,
	Paper,
	Grid,
	TextField,
} from '@mui/material';
import { getToDoPrioritys } from 'core/API/todo';
import { getToDoScheduleById } from 'core/API/recurringTodo';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import styles from '../addToDo/AddToDo.module.scss';
import {
	buildPriorityLookup,
	getPriorityDisplayValue,
	normalizePrioritiesList,
} from 'core/utils/todoScheduleEvent';
import {
	getScheduleAssignmentDisplay,
	getScheduleFrequencyDisplay,
	getScheduleTimeDisplay,
} from 'module/toDoPage/utils/recurringTodoUtils';

type RouteParams = {
	id: string;
};

const readonlyInputSx = {
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#fff',
	},
	'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
		borderColor: 'rgba(0,0,0,0.23)',
	},
	'& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
		borderColor: 'rgba(0,0,0,0.23)',
	},
} as const;

const getScheduleCategoryDisplay = (schedule: Record<string, unknown>): string => {
	const category = schedule.category;
	if (!category) return 'Uncategorized';
	if (typeof category === 'string') return category;
	const categoryRecord = category as Record<string, unknown>;
	return String(categoryRecord.label ?? categoryRecord.name ?? 'Uncategorized');
};

const ViewToDoSchedule = () => {
	const { id } = useParams<RouteParams>();
	const workplaceId = Number(localStorage.getItem('workplace-id')) || 1;

	const { data: schedule, isLoading, error } = useQuery(
		['to-do-schedule', id],
		() => getToDoScheduleById(Number(id)),
		{ enabled: !!id }
	);

	const { data: prioritiesData } = useQuery(['todo-priorities', workplaceId], () => getToDoPrioritys(), {
		enabled: !!workplaceId,
	});
	const priorityLookup = buildPriorityLookup(normalizePrioritiesList(prioritiesData));

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
				<CircularProgress />
			</Box>
		);
	}

	if (error || !schedule) {
		return (
			<Box mt={4}>
				<Alert severity="error">Failed to load recurring to-do series.</Alert>
			</Box>
		);
	}

	const scheduleRecord = schedule as Record<string, unknown>;
	const assignmentType =
		scheduleRecord.assignmentType === 'group' || scheduleRecord.group ? 'Group' : 'Person';

	return (
		<Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Button style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}>
					<NavLink
						to={ROUTES.TO_DO}
						style={{ textDecoration: 'none', color: 'white', padding: '2px 12px' }}
					>
						Back
					</NavLink>
				</Button>
			</Box>

			<Divider sx={{ mb: 2 }} />

			<Typography variant="h6" mb={2} className={styles.schedulerTitle}>
				View Recurring To-Do Series
			</Typography>

			<Grid container spacing={2}>
				<Grid item xs={6}>
					<TextField
						label="Assignment Type"
						value={assignmentType}
						fullWidth
						InputProps={{ readOnly: true }}
						sx={readonlyInputSx}
					/>
				</Grid>
				<Grid item xs={6}>
					<TextField
						label={assignmentType === 'Person' ? 'Selected Person' : 'Selected Group'}
						value={getScheduleAssignmentDisplay(scheduleRecord)}
						fullWidth
						InputProps={{ readOnly: true }}
						sx={readonlyInputSx}
					/>
				</Grid>

				<Grid item xs={12}>
					<TextField
						label="Description"
						fullWidth
						value={String(scheduleRecord.description ?? '')}
						InputProps={{ readOnly: true }}
						sx={readonlyInputSx}
					/>
				</Grid>

				<Grid item xs={6}>
					<TextField
						label="Priority"
						fullWidth
						value={getPriorityDisplayValue(scheduleRecord.priority, priorityLookup) ?? 'N/A'}
						InputProps={{ readOnly: true }}
						sx={readonlyInputSx}
					/>
				</Grid>
				<Grid item xs={6}>
					<TextField
						label="Category"
						fullWidth
						value={getScheduleCategoryDisplay(scheduleRecord)}
						InputProps={{ readOnly: true }}
						sx={readonlyInputSx}
					/>
				</Grid>

				<Grid item xs={6}>
					<TextField
						label="Frequency"
						fullWidth
						value={getScheduleFrequencyDisplay(scheduleRecord)}
						InputProps={{ readOnly: true }}
						sx={readonlyInputSx}
					/>
				</Grid>
				<Grid item xs={6}>
					<TextField
						label="Schedule"
						fullWidth
						value={getScheduleTimeDisplay(scheduleRecord)}
						InputProps={{ readOnly: true }}
						sx={readonlyInputSx}
					/>
				</Grid>

				<Grid item xs={12}>
					<TextField
						label="Notes"
						fullWidth
						multiline
						rows={3}
						value={String(scheduleRecord.note ?? '')}
						InputProps={{ readOnly: true }}
						sx={readonlyInputSx}
					/>
				</Grid>
			</Grid>
		</Paper>
	);
};

export default ViewToDoSchedule;
